/// <amd-module name="Test/Analytics/Widgets/OverallStateWidget" />
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import * as React from "react";
import { DonutChartVisual, IDonutChartVisualDatum, IDonutChartVisualProps } from "ReportsCommon/DonutChartVisual/Components/DonutChartVisual";
import { IconMapping, OverallStateWidgetConstants } from "Test/Analytics/Widgets/OverallStateWidgetConstants";
import { OutcomeToColorMap } from "Test/ManualTestReports/TestReportsCommon/Constants";
import * as Resources from "Test/Analytics/Widgets/Resources";
import { PieChart } from "TFS/Views/PieChart/PieChart";
import { IObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import { ITestProgressReportWidgetProps } from "Test/ManualTestReports/TestReportsCommon/ITestProgressReportWidgetProps";
import { BaseWidgetComponent } from "Test/ManualTestReports/TestReportsCommon/BaseWidgetComponent";

export interface IOverallStateWidgetProps extends ITestProgressReportWidgetProps {
    testPlanCount: number;
    testPointCount: number;
    testPointExecuted: number;
    executionPercentageCount: number;
    passPercentageCount: number;
    outcomeMappingCount: {
        passedCount: number;
        failedCount: number;
        blockedCount: number;
        notApplicableCount: number;
        otherCount: number;
    },
    loading?: boolean;
    errorText?: string;
}

export interface IOverallStateWidgetInputProps {
    props: IObservableValue<IOverallStateWidgetProps>;
}

export class OverallStateWidget extends BaseWidgetComponent<IOverallStateWidgetProps> {
    
    private _pieChartColors: string[] = [];

    constructor(props: IOverallStateWidgetProps, context: IVssComponentContext) {
        super(props, context);
    }

    public render() {
        this.logRenderingStart();
        const props = this._getProps();

        let elementToDisplay = (): JSX.Element => {
            if (props.loading === true) {
                return <Spinner className="overall-state-widget-spinner flex-grow flex-column" size={SpinnerSize.large} />
            }
            else {
                return <AnalyticsUnavailableMessage suggestion={this.props.errorText} />
            }
        };


        if (props.loading || props.errorText) {
            return (
                <Card className="overall-state-container flex-grow" titleProps={{ text: Resources.WidgetTitle }}>
                    {elementToDisplay()}
                </Card>);
        }

        else {
            return (
                <Card className="overall-state-container content-available flex-grow" titleProps={{ text: Resources.WidgetTitle, className: "title-s" }}>
                    {getComponent("", IconMapping.TestPlan, props.testPlanCount.toString(), Resources.TestPlans)}
                    {getComponent("column-2", IconMapping.TestPoint, props.testPointCount.toString(), Resources.TestPoints)}
                    {getComponent("", IconMapping.TestPoint, props.testPointExecuted.toString(), Resources.TestsRun, `${props.testPointExecuted.toString()} / ${props.testPointCount.toString()}`)}
                    {this._getTestExecutionStatusVisual()}
                    {getComponent("", IconMapping.PassRate, this.props.passPercentageCount + "%", Resources.PassRate, `${props.outcomeMappingCount.passedCount.toString()} / ${props.testPointExecuted.toString()}`)}
                    {this._renderOutcomeChart()}
                </Card>
            );
        }
    }

    protected isLoading(): boolean {
        return this.props.loading? this.props.loading: false;
    }

    private _renderOutcomeChart(): JSX.Element {
        return <DonutChartVisual className="donutChart" {...this._getDonutChartProps()} />
    }

    private _getDonutChartProps(): IDonutChartVisualProps {
        const props = this.props;

        let donutChartProps: IDonutChartVisualProps = {};

        donutChartProps.chartData = [];

        if (props.outcomeMappingCount.passedCount > 0) {
            donutChartProps.chartData.push({
                name: Resources.PassedString,
                legendLabel: Resources.PassedString,
                color: OutcomeToColorMap.Passed,
                y: props.outcomeMappingCount.passedCount,
                legendDisplayValue: props.outcomeMappingCount.passedCount.toString()
            });
        }
        if (props.outcomeMappingCount.failedCount > 0) {
            donutChartProps.chartData.push({
                name: Resources.FailedString,
                legendLabel: Resources.FailedString,
                color: OutcomeToColorMap.Failed,
                y: props.outcomeMappingCount.failedCount,
                legendDisplayValue: props.outcomeMappingCount.failedCount.toString()
            } as IDonutChartVisualDatum);
        }

        if (props.outcomeMappingCount.blockedCount > 0) {
            donutChartProps.chartData.push({
                name: Resources.BlockedString,
                legendLabel: Resources.BlockedString,
                color: OutcomeToColorMap.Blocked,
                y: props.outcomeMappingCount.blockedCount,
                legendDisplayValue: props.outcomeMappingCount.blockedCount.toString()
            } as IDonutChartVisualDatum);
        }
        if (props.outcomeMappingCount.notApplicableCount > 0) {
            donutChartProps.chartData.push({
                name: Resources.NotApplicable,
                legendLabel: Resources.NotApplicable,
                color: OutcomeToColorMap.NotApplicable,
                y: props.outcomeMappingCount.notApplicableCount,
                legendDisplayValue: props.outcomeMappingCount.notApplicableCount.toString()
            } as IDonutChartVisualDatum);
        }

        if (props.outcomeMappingCount.otherCount > 0) {
            donutChartProps.chartData.push({ 
                name: Resources.OtherString,
                legendLabel: Resources.OtherString,
                color: OutcomeToColorMap.Other,
                y: props.outcomeMappingCount.otherCount,
                legendDisplayValue: props.outcomeMappingCount.otherCount.toString()
            } as IDonutChartVisualDatum);
        }

        donutChartProps.pageContext = this.context.pageContext;
        donutChartProps.chartDiameter = OverallStateWidgetConstants.DonutChartDiameter;
        donutChartProps.legendOptions = {
            className : "overall-state-outcome-legend",
            diameter: 12
        };

        return donutChartProps;
    }

    private _getExecutionPieChartProps() {
        return {

            chartProps: {
                data: [this.props.testPointExecuted, this.props.testPointCount - this.props.testPointExecuted],
                graphHeight: OverallStateWidgetConstants.PieChartHeight,
                graphWidth: OverallStateWidgetConstants.PieChartWidth,
                outerRadius: OverallStateWidgetConstants.PieChartRadius,
                colors: this._pieChartColors,
                innerRadius: OverallStateWidgetConstants.PieChartRadius - 5,
                pieChartText: this.props.executionPercentageCount.toString().concat("%"),
                pieChartClass: "pie-chart",
                textClass: "pie-chart-text flex-column justify-center"
            },
            text: Resources.ExecutedText
        }
    }

    private _getTestExecutionStatusVisual(): JSX.Element {
        const props = this._getProps();
        const themeService: IVssThemeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService")
        this._pieChartColors = [`rgba(${themeService.getThemeValue("palette-accent2-dark")}, 1)`, `rgba(${themeService.getThemeValue("palette-neutral-4")}, 1)`];

        return (<div className="test-execution-visual">
            <PieChart {...this._getExecutionPieChartProps().chartProps} />
            <span>{this._getExecutionPieChartProps().text}</span>
        </div>);
    }

    private _getProps(): IOverallStateWidgetProps {
        return this.props;
    }
}

const getComponent = (containerClassName: string, iconName: string, firstLinePrimaryText: string, secondLineText?: string, firstLineSecondaryText?: string): JSX.Element => {
    return (
        <div className={css(containerClassName, "flex flex-grow", "state-widget")}>
            <Icon iconName={iconName} className={"font-size-ml icon-container"} />
            <div className="count-and-text">
                {!!firstLineSecondaryText ? <div><span className="title-xs">{firstLinePrimaryText}</span> <span >({firstLineSecondaryText})</span> </div> : <div><span className="title-xs">{firstLinePrimaryText}</span></div>}
                <div className="text fontSize">{secondLineText}</div>
            </div>
        </div>
    );
};